import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import React, { useContext, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import Icon from 'components/Icon'
import { getLogo } from 'components/scenarios/create/status-checks/third-party-presets'
import {
  AuthenticationFormValues,
  getAWSDomains,
  getExternalIntegrationForObservabilityTool,
  getUnsavedObservabilityTools,
  ObservabilityTool,
  OBSERVABILITY_TOOLS,
  setObservabilityToolAndDomain
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolDomainSelect } from 'components/status-checks/form/authentication/ObservabilityToolDomainSelect'
import {
  LIST_ITEM_MIN_WIDTH,
  StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  ObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'
import {
  ObservabilityToolType,
  TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import { getIntegrationTypeFromObservabilityToolTypeAndDomain } from 'utils/team-external-integrations/team-external-integrations-util'

const ObservabilityToolMenuItemContent: React.FC<{
  observabilityTool: ObservabilityTool
}> = ({ observabilityTool }) => {
  return (
    <Stack
      alignItems="center"
      direction="row"
      // The fontSizes value is used here to match the height of the Icon/img components in the ListItemIcon. This is
      // used to prevent the Select textfield component from expanding and causing a misalignment of UI components.
      sx={{ maxHeight: theme.fontSizes[4] }}
    >
      <ListItemIcon sx={{ minWidth: LIST_ITEM_MIN_WIDTH }}>
        {observabilityTool.type === ObservabilityToolType.CUSTOM ? (
          <Icon
            css={{
              height: theme.fontSizes[4],
              width: theme.fontSizes[4]
            }}
            icon="regularTelescope"
          />
        ) : (
          <img
            alt={observabilityTool.name}
            css={{
              color: theme.colors.gray[1],
              width: theme.fontSizes[4],
              height: theme.fontSizes[4]
            }}
            src={getLogo(observabilityTool.type.toLowerCase())}
          />
        )}
      </ListItemIcon>
      <ListItemText>{observabilityTool.name}</ListItemText>
    </Stack>
  )
}

export const ObservabilityToolAndDomainSelect: React.FC<{
  externalIntegrations: TeamExternalIntegration[]
  isEdit: boolean
  selectedObservabilityTool: ObservabilityTool
}> = ({ externalIntegrations, isEdit, selectedObservabilityTool }) => {
  const { trans } = useTranslation()
  const form = useFormContext<
    StatusCheckFormValues | AuthenticationFormValues
  >()
  const { setAuthenticationResponse, isCreatingAuthenticationOnly } =
    useContext(ObservabilityToolAuthenticationContext)

  const { control, formState, trigger } = form

  const customObservabilityTools: ObservabilityTool[] = externalIntegrations
    .filter(
      (externalIntegration) =>
        externalIntegration.observabilityToolType ===
        ObservabilityToolType.CUSTOM
    )
    .map((customExternalIntegration) => ({
      name: customExternalIntegration.name,
      domains: customExternalIntegration.domain
        ? [
            {
              name: customExternalIntegration.domain,
              integrationType: IntegrationType.Custom
            }
          ]
        : [],
      type: ObservabilityToolType.CUSTOM,
      integrationType: IntegrationType.Custom
    }))

  // dynamically adding aws domains
  const AWSDomains = getAWSDomains(externalIntegrations)

  const observabilityTools = useMemo(() => {
    if (isCreatingAuthenticationOnly) {
      return getUnsavedObservabilityTools(externalIntegrations, AWSDomains)
    }

    return [...OBSERVABILITY_TOOLS].map((observabilityTool) => {
      const newObservabilityTool = { ...observabilityTool }
      if (observabilityTool.name === 'AWS') {
        newObservabilityTool.domains = AWSDomains
      }

      return newObservabilityTool
    })
  }, [AWSDomains, externalIntegrations, isCreatingAuthenticationOnly])

  const allObservabilityTools: ObservabilityTool[] = useMemo(() => {
    const observabilityToolsCopy = Array.from(observabilityTools)

    // Adds custom observability tools to the dropdown list
    return [
      ...observabilityToolsCopy,
      ...customObservabilityTools
    ] as ObservabilityTool[]
  }, [customObservabilityTools, observabilityTools])

  const handleSelectObservabilityTool = async (e, field) => {
    const newObservabilityTool = allObservabilityTools.find(
      (n) => n.name === e.target.value
    )

    const newSelectedDomain = newObservabilityTool?.domains[0]?.name

    newObservabilityTool.integrationType =
      getIntegrationTypeFromObservabilityToolTypeAndDomain(
        newObservabilityTool.type,
        newSelectedDomain
      )

    const newObservabilityToolWithSelectedDomain = {
      ...newObservabilityTool,
      selectedDomain: newObservabilityTool.domains[0]?.name
    }

    const observabilityTool =
      newObservabilityTool.type === ObservabilityToolType.AWS
        ? newObservabilityToolWithSelectedDomain
        : newObservabilityTool

    const externalIntegration = getExternalIntegrationForObservabilityTool(
      observabilityTool,
      externalIntegrations
    )

    setObservabilityToolAndDomain(
      observabilityTool,
      null,
      externalIntegration,
      form
    )

    // Reset endpoint response
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)

    // Validate headers whenever observability tool changes
    await trigger('headers')

    field.onChange(e)
  }

  const handleSelectDomain = async (e, field) => {
    const selectedDomain = selectedObservabilityTool?.domains.find(
      (d) => d.name === e.target.value
    )

    const newObservabilityTool = {
      ...selectedObservabilityTool,
      integrationType: selectedDomain.integrationType
    }
    const externalIntegration = getExternalIntegrationForObservabilityTool(
      newObservabilityTool,
      externalIntegrations
    )

    setObservabilityToolAndDomain(
      newObservabilityTool,
      selectedDomain,
      externalIntegration,
      form
    )

    // Reset endpoint response
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)

    // Validate headers whenever domain changes
    await trigger('headers')

    field.onChange(e)
  }

  return (
    <>
      <Typography>
        {trans(
          'forms.save_status_check.select_observability_tool_and_domain.observability_tool.prompt'
        )}
      </Typography>
      <Stack direction="row" gap={2}>
        <FormControl sx={{ width: '50%' }}>
          <InputLabel>
            {trans(
              'forms.save_status_check.select_observability_tool_and_domain.observability_tool.select_field.label'
            )}
          </InputLabel>
          <Controller
            control={control}
            name="observabilityTool.name"
            render={({ field }) => (
              <Select
                {...field}
                data-testid="observability-tool-dropdown-select-name"
                disabled={isEdit}
                error={Boolean(formState.errors.observabilityTool)}
                label={trans(
                  'forms.save_status_check.select_observability_tool_and_domain.observability_tool.select_field.label'
                )}
                labelId="select-observability-tool-label"
                onChange={(e) => handleSelectObservabilityTool(e, field)}
              >
                {observabilityTools.map((observabilityTool) => (
                  <MenuItem
                    key={observabilityTool.name}
                    data-testid={`observability-tool-integration-type-${observabilityTool.integrationType}`}
                    value={observabilityTool.name}
                  >
                    <ObservabilityToolMenuItemContent
                      observabilityTool={observabilityTool}
                    />
                  </MenuItem>
                ))}
                {customObservabilityTools.length > 0 && (
                  <ListSubheader key="custom-observability-tool-subheader">
                    {trans(
                      'forms.save_status_check.select_observability_tool_and_domain.observability_tool.custom.list_subheader.label'
                    )}
                  </ListSubheader>
                )}
                {customObservabilityTools.map((observabilityTool) => (
                  <MenuItem
                    key={observabilityTool.name}
                    value={observabilityTool.name}
                  >
                    <ObservabilityToolMenuItemContent
                      observabilityTool={observabilityTool}
                    />
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <FormHelperText error={Boolean(formState.errors.observabilityTool)}>
            {formState.errors.observabilityTool?.message}
          </FormHelperText>
        </FormControl>
        {selectedObservabilityTool?.type !== ObservabilityToolType.AWS &&
          selectedObservabilityTool?.domains?.length > 0 && (
            <ObservabilityToolDomainSelect
              handleSelectDomain={handleSelectDomain}
              isEdit={isEdit}
              selectedObservabilityTool={selectedObservabilityTool}
            />
          )}
      </Stack>
    </>
  )
}
